import { formatDate, formatDateTime } from "./formatters";

export type ChartPoint = { t: number; v: number; dateOnly?: boolean };
export type PriceObservation = { time: string | number; value: number };

/** Daily backend observations now use SQL-style midnight dates without a timezone. */
function observationDate(value: string | number): string | null {
  return typeof value === "string" ? /^(\d{4}-\d{2}-\d{2})(?: 00:00:00(?:\.0{1,6})?)?$/.exec(value.trim())?.[1] ?? null : null;
}

export function chartTimestamp(value: string | number): number | null {
  const date = observationDate(value);
  if (date) {
    const timestamp = Date.parse(`${date}T00:00:00Z`);
    return Number.isFinite(timestamp) && new Date(timestamp).toISOString().slice(0, 10) === date ? timestamp : null;
  }
  const numeric = typeof value === "number" ? value : /^\d+(?:\.\d+)?$/.test(value.trim()) ? Number(value) : null;
  const timestamp = numeric === null && typeof value === "string"
    ? (/^\d{4}-\d{2}-\d{2}$/.test(value) || /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? Date.parse(value) : NaN)
    : numeric === null ? NaN : numeric < 1e12 ? numeric * 1000 : numeric;
  return Number.isFinite(timestamp) && timestamp >= 0 && timestamp <= 8.64e15 ? timestamp : null;
}

export function normalizeChartPoints(points: readonly ChartPoint[]): ChartPoint[] {
  const byTime = new Map<number, ChartPoint>();
  for (const point of points) {
    if (Number.isFinite(point.t) && Number.isFinite(point.v)) byTime.set(point.t, point);
  }
  return [...byTime.values()].sort((a, b) => a.t - b.t);
}

export function priceObservationsToPoints(prices: readonly PriceObservation[]): ChartPoint[] {
  return normalizeChartPoints(prices.flatMap((point) => {
    const timestamp = chartTimestamp(point.time);
    return timestamp === null ? [] : [{ t: timestamp, v: point.value, ...(observationDate(point.time) ? { dateOnly: true } : {}) }];
  }));
}

export function filterChartPeriod(points: readonly ChartPoint[], days: number | null, now = Date.now()): ChartPoint[] {
  return points.filter((point) => point.t <= now && (days === null || point.t >= now - days * 86_400_000));
}

export function formatChartTime(point: ChartPoint) {
  return point.dateOnly ? formatDate(point.t, { dateStyle: "medium", timeZone: "UTC" }) : formatDateTime(point.t);
}
